import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "../chart";
import {
  ChartDataTable,
  type ChartDatum,
  type ChartDataTableLabels,
} from "./ChartDataTable";

export type MetricChartKind = "bar" | "line";

export interface MetricChartProps {
  /** The metric's display name (used for the chart's accessible name + data table). */
  metricKey: string;
  data: ChartDatum[];
  kind: MetricChartKind;
  /** Which --chart-N series colour to use (1..5). Defaults to 1. */
  series?: 1 | 2 | 3 | 4 | 5;
  /** Visible "no data" copy shown in the chart body when there are no rows. */
  emptyLabel?: string;
  /** i18n-resolved labels for the mandatory sr-only data table (caller-supplied). */
  dataTableLabels: ChartDataTableLabels;
}

/**
 * A Recharts chart (D-07) themed through `var(--chart-N)` CSS custom properties
 * so it re-themes automatically in dark mode (the .dark block redefines
 * --chart-N in the consumer's tokens). `accessibilityLayer` is ON so the SVG is
 * keyboard navigable.
 *
 * The chart sits inside a Card with a visible title so it reads as an
 * intentional, labelled panel rather than a floating SVG. Fail-soft: when there
 * are no rows the body shows a muted "no data" line in place of the chart.
 *
 * CRITICAL (RESEARCH Pitfall 6 / UI-SPEC Accessibility / SC 1.1.1): the chart is
 * ALWAYS rendered together with a `ChartDataTable` sibling conveying the same
 * numbers. A green vitest-axe run is NOT sufficient for an SVG chart's text
 * alternative — the data table is the required, asserted alternative.
 */
export function MetricChart({
  metricKey,
  data,
  kind,
  series = 1,
  emptyLabel,
  dataTableLabels,
}: MetricChartProps) {
  // Theme the single "value" series through a CSS custom property (NOT a literal
  // hex) so tenant branding + dark mode flow through automatically.
  const chartConfig: ChartConfig = {
    value: {
      label: metricKey,
      color: `var(--chart-${series})`,
    },
  };

  const hasRows = data.length > 0;

  return (
    <Card className="gap-0 py-0">
      <CardHeader className="border-b px-5 py-4">
        <CardTitle className="text-sm font-semibold">{metricKey}</CardTitle>
      </CardHeader>
      <CardContent className="p-5">
        {hasRows ? (
          <ChartContainer
            config={chartConfig}
            // role=img + an accessible name make the chart a single nameable region;
            // the real numbers live in the sibling data table below.
            role="img"
            aria-label={metricKey}
            className="h-56 w-full"
          >
            {kind === "bar" ? (
              <BarChart accessibilityLayer data={data} barCategoryGap={8}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="label"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                />
                <YAxis tickLine={false} axisLine={false} width={40} />
                <ChartTooltip content={<ChartTooltipContent />} />
                {/* ATLAS-05: emphasized bar = full accent (chart-1) + dark glow;
                    regular bars alternate accent 55%/30% (chart-2/chart-3).
                    Colors flow only through --chart-* tokens (no literals). */}
                <Bar dataKey="value" radius={[6, 2, 0, 0]}>
                  {data.map((datum, i) => (
                    <Cell
                      key={`bar-${datum.label}-${i}`}
                      fill={
                        datum.emphasized
                          ? "var(--chart-1)"
                          : i % 2 === 0
                            ? "var(--chart-2)"
                            : "var(--chart-3)"
                      }
                      className={
                        datum.emphasized
                          ? "dark:[filter:drop-shadow(0_0_12px_color-mix(in_oklch,var(--chart-1)_40%,transparent))]"
                          : undefined
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            ) : (
              <LineChart accessibilityLayer data={data}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="label"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                />
                <YAxis tickLine={false} axisLine={false} width={40} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Line
                  dataKey="value"
                  type="monotone"
                  stroke="var(--color-value)"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            )}
          </ChartContainer>
        ) : (
          <div className="text-muted-foreground flex h-56 items-center justify-center text-sm">
            {emptyLabel}
          </div>
        )}
        <ChartDataTable rows={data} labels={dataTableLabels} />
      </CardContent>
    </Card>
  );
}
